import React, { useState, useEffect } from 'react';
import './PrototypeNav.css';

interface PrototypeNavProps {
  currentScreen?: string;
  onNavigate?: (screen: string, path?: string) => void;
}

const PrototypeNav: React.FC<PrototypeNavProps> = ({ currentScreen, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeScreen, setActiveScreen] = useState(currentScreen || 'splash');

  useEffect(() => {
    if (currentScreen) {
      setActiveScreen(currentScreen);
    }
  }, [currentScreen]);

  const specialScreens = [
    { name: 'Splash Screen', icon: '🚀', screen: 'splash' },
    { name: 'Onboarding', icon: '📱', screen: 'onboarding' }
  ];

  const pages = [
    { path: '/', name: 'Home', icon: '🏠', screen: 'home' },
    { path: '/categories', name: 'Categories', icon: '📚', screen: 'categories' },
    { path: '/calendar', name: 'Calendar', icon: '📅', screen: 'calendar' },
    { path: '/store', name: 'Store', icon: '🛒', screen: 'store' },
    { path: '/login', name: 'Login', icon: '🔐', screen: 'login' },
    { path: '/otp', name: 'OTP Verification', icon: '🔢', screen: 'otp' },
    { path: '/profile', name: 'Profile', icon: '👤', screen: 'profile' },
    { path: '/support', name: 'Support', icon: '❓', screen: 'support' },
    { path: '/forgot-password', name: 'Forgot Password', icon: '🔑', screen: 'forgot-password' },
    { path: '/delete-account', name: 'Delete Account', icon: '🗑️', screen: 'delete-account' }
  ];

  const coursePages = [
    { path: '/course/1', name: 'Course Details', icon: '📖', screen: 'course-details' },
    { path: '/course/1/chapters', name: 'Chapters List', icon: '📑', screen: 'chapters-list' },
    { path: '/course/1/chapter/1', name: 'Chapter Details', icon: '📄', screen: 'chapter-details' },
    { path: '/course/1/chapter/1/video/1', name: 'Video Player', icon: '▶️', screen: 'video-player' },
    { path: '/course/1/chapter/1/quiz/1', name: 'Quiz Attempt', icon: '📝', screen: 'quiz-attempt' },
    { path: '/course/1/chapter/1/quiz/1/results', name: 'Quiz Results', icon: '✅', screen: 'quiz-results' }
  ];

  const liveSessionPages = [
    { path: '/live-sessions', name: 'Live Sessions List', icon: '🔴', screen: 'live-sessions-list' },
    { path: '/live-session/1', name: 'Live Session Details', icon: '📹', screen: 'live-session-details' },
    { path: '/live-session/1/room', name: 'Live Session Room', icon: '💻', screen: 'live-session-room' },
    { path: '/online-course/1', name: 'Online Course Details', icon: '📚', screen: 'online-course-details' },
    { path: '/online-course/1/session/1', name: 'Session Details', icon: '🔴', screen: 'session-details' }
  ];

  const paymentPages = [
    { path: '/payment/1/proof', name: 'Payment Proof', icon: '💳', screen: 'payment-proof' },
    { path: '/payment/1/status', name: 'Payment Status', icon: '📊', screen: 'payment-status' }
  ];

  const profilePages = [
    { path: '/profile/edit', name: 'Edit Profile', icon: '✏️', screen: 'edit-profile' },
    { path: '/profile/courses', name: 'My Courses', icon: '📚', screen: 'my-courses' },
    { path: '/profile/quizzes', name: 'Quizzes', icon: '📝', screen: 'quizzes' },
    { path: '/profile/books', name: 'My Books', icon: '📖', screen: 'my-books' },
    { path: '/orders', name: 'Orders', icon: '📋', screen: 'orders' },
    { path: '/profile/about', name: 'About', icon: 'ℹ️', screen: 'about' },
    { path: '/profile/privacy', name: 'Privacy Policy', icon: '🔒', screen: 'privacy' },
    { path: '/settings', name: 'Settings', icon: '⚙️', screen: 'settings' }
  ];

  const seeAllPages = [
    { path: '/courses/all', name: 'All Courses', icon: '📚', screen: 'all-courses' },
    { path: '/resources/all', name: 'All Resources', icon: '📖', screen: 'all-resources' },
    { path: '/resource/1', name: 'Resource Details', icon: '📄', screen: 'resource-details' },
    { path: '/live-sessions/all', name: 'All Live Sessions', icon: '🔴', screen: 'all-live-sessions' }
  ];

  const handleNavigate = (screen: string, path?: string) => {
    setActiveScreen(screen);
    if (onNavigate) {
      // Pass both screen and path to onNavigate
      // The wrapper will handle React Router navigation
      onNavigate(screen, path);
    } else if (path) {
      // Fallback for navigation outside Router context
      if (window.location.pathname !== path) {
        window.location.href = path;
      }
    }
  };

  return (
    <div className={`prototype-nav ${isOpen ? 'open' : 'closed'}`}>
      <button 
        className="prototype-nav-toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? '◀' : '▶'}
      </button>
      
      {isOpen && (
        <div className="prototype-nav-content">
          <div className="prototype-nav-header">
            <h3>Prototype Navigation</h3>
            <span className="prototype-badge">DEMO</span>
          </div>
          
          <div className="prototype-nav-list">
            {/* Special Screens */}
            {specialScreens.map((screen) => (
              <button
                key={screen.screen}
                className={`prototype-nav-item ${activeScreen === screen.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(screen.screen)}
              >
                <span className="prototype-nav-icon">{screen.icon}</span>
                <span className="prototype-nav-name">{screen.name}</span>
                {activeScreen === screen.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}
            
            <div className="prototype-nav-divider"></div>
            
            {/* Regular Pages */}
            {pages.map((page) => (
              <button
                key={page.path}
                className={`prototype-nav-item ${activeScreen === page.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(page.screen, page.path)}
              >
                <span className="prototype-nav-icon">{page.icon}</span>
                <span className="prototype-nav-name">{page.name}</span>
                {activeScreen === page.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}

            <div className="prototype-nav-divider"></div>
            <div className="prototype-nav-group-title">Course Pages</div>
            {coursePages.map((page) => (
              <button
                key={page.path}
                className={`prototype-nav-item nested ${activeScreen === page.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(page.screen, page.path)}
              >
                <span className="prototype-nav-icon">{page.icon}</span>
                <span className="prototype-nav-name">{page.name}</span>
                {activeScreen === page.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}

            <div className="prototype-nav-divider"></div>
            <div className="prototype-nav-group-title">Live Sessions</div>
            {liveSessionPages.map((page) => (
              <button
                key={page.path}
                className={`prototype-nav-item nested ${activeScreen === page.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(page.screen, page.path)}
              >
                <span className="prototype-nav-icon">{page.icon}</span>
                <span className="prototype-nav-name">{page.name}</span>
                {activeScreen === page.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}

            <div className="prototype-nav-divider"></div>
            <div className="prototype-nav-group-title">Payment</div>
            {paymentPages.map((page) => (
              <button
                key={page.path}
                className={`prototype-nav-item nested ${activeScreen === page.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(page.screen, page.path)}
              >
                <span className="prototype-nav-icon">{page.icon}</span>
                <span className="prototype-nav-name">{page.name}</span>
                {activeScreen === page.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}

            <div className="prototype-nav-divider"></div>
            <div className="prototype-nav-group-title">Profile Pages</div>
            {profilePages.map((page) => (
              <button
                key={page.path}
                className={`prototype-nav-item nested ${activeScreen === page.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(page.screen, page.path)}
              >
                <span className="prototype-nav-icon">{page.icon}</span>
                <span className="prototype-nav-name">{page.name}</span>
                {activeScreen === page.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}

            <div className="prototype-nav-divider"></div>
            <div className="prototype-nav-group-title">See All Pages</div>
            {seeAllPages.map((page) => (
              <button
                key={page.path}
                className={`prototype-nav-item nested ${activeScreen === page.screen ? 'active' : ''}`}
                onClick={() => handleNavigate(page.screen, page.path)}
              >
                <span className="prototype-nav-icon">{page.icon}</span>
                <span className="prototype-nav-name">{page.name}</span>
                {activeScreen === page.screen && (
                  <span className="prototype-nav-indicator">●</span>
                )}
              </button>
            ))}
          </div>
          
          <div className="prototype-nav-footer">
            <p>Click to navigate between screens</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrototypeNav;
